import React from 'react';

/**
 * SVG Pool Table Component
 * Renders a pool table in portrait orientation (standing at the head of the table).
 *
 * Orientation:
 * - Head rail (Kopfbande) at BOTTOM - where you stand
 * - Foot rail (Fußbande) at TOP - where the rack is placed
 * - Head spot at bottom, Foot spot at top
 *
 * Supports multiple table sizes:
 * - 7ft: 2000mm x 1000mm (bar table)
 * - 8ft: 2240mm x 1120mm (standard)
 * - 9ft: 2540mm x 1270mm (tournament)
 * - 10ft Snooker: 3048mm x 1524mm
 * - 12ft Snooker: 3569mm x 1778mm
 */

// Table size configurations (playing surface in mm, real dimensions)
const TABLE_SIZES = {
  '7ft': { width: 1000, height: 2000, name: '7-Fuß (Bar)', pockets: 6, diamonds: { long: 8, short: 4 } },
  '8ft': { width: 1120, height: 2240, name: '8-Fuß (Standard)', pockets: 6, diamonds: { long: 8, short: 4 } },
  '9ft': { width: 1270, height: 2540, name: '9-Fuß (Turnier)', pockets: 6, diamonds: { long: 8, short: 4 } },
  '10ft-snooker': { width: 1524, height: 3048, name: '10-Fuß Snooker', pockets: 6, diamonds: { long: 12, short: 6 } },
  '12ft-snooker': { width: 1778, height: 3569, name: '12-Fuß Snooker', pockets: 6, diamonds: { long: 12, short: 6 } },
};

// Ball colors for pool (standard American pool)
const POOL_BALL_COLORS = {
  0: { fill: '#FFFFFF', stroke: '#333333', label: '', name: 'Cue Ball' },
  1: { fill: '#FFD700', stroke: '#B8860B', label: '1', name: 'Gelb' },
  2: { fill: '#0000FF', stroke: '#00008B', label: '2', name: 'Blau' },
  3: { fill: '#FF0000', stroke: '#8B0000', label: '3', name: 'Rot' },
  4: { fill: '#800080', stroke: '#4B0082', label: '4', name: 'Lila' },
  5: { fill: '#FF6600', stroke: '#CC5200', label: '5', name: 'Orange' },
  6: { fill: '#006400', stroke: '#004B00', label: '6', name: 'Grün' },
  7: { fill: '#8B0000', stroke: '#5C0000', label: '7', name: 'Braun' },
  8: { fill: '#000000', stroke: '#333333', label: '8', name: 'Schwarz' },
  9: { fill: '#FFD700', stroke: '#B8860B', label: '9', striped: true, name: 'Gelb gestreift' },
  10: { fill: '#0000FF', stroke: '#00008B', label: '10', striped: true, name: 'Blau gestreift' },
  11: { fill: '#FF0000', stroke: '#8B0000', label: '11', striped: true, name: 'Rot gestreift' },
  12: { fill: '#800080', stroke: '#4B0082', label: '12', striped: true, name: 'Lila gestreift' },
  13: { fill: '#FF6600', stroke: '#CC5200', label: '13', striped: true, name: 'Orange gestreift' },
  14: { fill: '#006400', stroke: '#004B00', label: '14', striped: true, name: 'Grün gestreift' },
  15: { fill: '#8B0000', stroke: '#5C0000', label: '15', striped: true, name: 'Braun gestreift' },
};

// Snooker ball colors
const SNOOKER_BALL_COLORS = {
  0: { fill: '#FFFFFF', stroke: '#333333', label: '', name: 'Cue Ball' },
  // 15 red balls (numbered r1-r15 internally, displayed without number)
  'r1': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 1' },
  'r2': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 2' },
  'r3': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 3' },
  'r4': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 4' },
  'r5': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 5' },
  'r6': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 6' },
  'r7': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 7' },
  'r8': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 8' },
  'r9': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 9' },
  'r10': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 10' },
  'r11': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 11' },
  'r12': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 12' },
  'r13': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 13' },
  'r14': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 14' },
  'r15': { fill: '#CC0000', stroke: '#990000', label: '', name: 'Rot 15' },
  // Colored balls (with point values as labels)
  'yellow': { fill: '#FFD700', stroke: '#B8860B', label: '2', name: 'Gelb (2)' },
  'green': { fill: '#228B22', stroke: '#006400', label: '3', name: 'Grün (3)' },
  'brown': { fill: '#8B4513', stroke: '#5D2906', label: '4', name: 'Braun (4)' },
  'blue': { fill: '#0000CD', stroke: '#00008B', label: '5', name: 'Blau (5)' },
  'pink': { fill: '#FF69B4', stroke: '#DB7093', label: '6', name: 'Pink (6)' },
  'black': { fill: '#1a1a1a', stroke: '#000000', label: '7', name: 'Schwarz (7)' },
};

// Ball sets configuration
const BALL_SETS = {
  'universal': {
    name: 'Universal (15+1)',
    balls: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    colors: POOL_BALL_COLORS,
  },
  '9-ball': {
    name: '9-Ball',
    balls: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    colors: POOL_BALL_COLORS,
  },
  '10-ball': {
    name: '10-Ball',
    balls: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    colors: POOL_BALL_COLORS,
  },
  '8-ball': {
    name: '8-Ball / 14.1',
    balls: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    colors: POOL_BALL_COLORS,
  },
  'snooker': {
    name: 'Snooker',
    balls: [0, 'r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'r7', 'r8', 'r9', 'r10', 'r11', 'r12', 'r13', 'r14', 'r15', 'yellow', 'green', 'brown', 'blue', 'pink', 'black'],
    colors: SNOOKER_BALL_COLORS,
  },
};

// Legacy export for backward compatibility
const BALL_COLORS = POOL_BALL_COLORS;

// Game types for rack shapes
const GAME_TYPES = {
  '8-ball': { rackShape: 'triangle', balls: 15 },
  '9-ball': { rackShape: 'diamond', balls: 9 },
  '10-ball': { rackShape: 'triangle', balls: 10 },
  '14.1': { rackShape: 'triangle', balls: 15 },
  'snooker': { rackShape: 'triangle', balls: 15 },
};

// Virtual grid size for scale-independent ball positions
// Ball positions are stored as grid coordinates (0 to GRID_WIDTH/HEIGHT)
// and converted to SVG coordinates based on current scale
const GRID_WIDTH = 1000;   // Virtual grid width
const GRID_HEIGHT = 2000;  // Virtual grid height (2:1 ratio for pool table)

const PoolTableSVG = ({
  tableSize = '9ft',
  gameType = '8-ball',
  ballSet = 'universal',  // Ball set to use
  scale = 0.25,  // Scale factor to convert mm to pixels
  balls = [],
  onBallClick,
  onBallDrag,
  onTableClick,
  selectedBall = null,
  interactive = true,
  showGrid = false,
  showRack = true,
  cueHitPoint = null,  // { x: -1 to 1, y: -1 to 1 } for cue ball hit point
  useGridCoordinates = false,  // If true, ball positions are grid coords (0-1000, 0-2000)
}) => {
  const tableConfig = TABLE_SIZES[tableSize] || TABLE_SIZES['9ft'];
  const gameConfig = GAME_TYPES[gameType] || GAME_TYPES['8-ball'];
  const ballSetConfig = BALL_SETS[ballSet] || BALL_SETS['universal'];

  // Calculate dimensions (portrait: width < height)
  const tableWidth = tableConfig.width * scale;
  const tableHeight = tableConfig.height * scale;

  // Padding and rail dimensions
  const padding = 20;
  const railWidth = 16;
  const frameWidth = 8;

  // Total SVG dimensions
  const svgWidth = tableWidth + 2 * padding + 2 * railWidth + 2 * frameWidth;
  const svgHeight = tableHeight + 2 * padding + 2 * railWidth + 2 * frameWidth;

  // Playing surface bounds
  const playingX = padding + frameWidth + railWidth;
  const playingY = padding + frameWidth + railWidth;
  const playingWidth = tableWidth;
  const playingHeight = tableHeight;

  // Ball radius (proportional to table size, ~57mm diameter for standard ball)
  const ballRadius = 28.5 * scale;

  // Pocket radius
  const pocketRadius = ballRadius * 1.8;

  // Diamond spacing
  const diamondSpacingX = playingWidth / tableConfig.diamonds.short;
  const diamondSpacingY = playingHeight / tableConfig.diamonds.long;

  // Pocket positions (portrait orientation)
  // Corner pockets at corners, side pockets on the LONG sides (left and right at center)
  const pockets = [
    { x: playingX, y: playingY, id: 'top-left' },                           // Top-left corner
    { x: playingX + playingWidth, y: playingY, id: 'top-right' },           // Top-right corner
    { x: playingX - 3, y: playingY + playingHeight / 2, id: 'left-center' }, // Left side (center)
    { x: playingX + playingWidth + 3, y: playingY + playingHeight / 2, id: 'right-center' }, // Right side (center)
    { x: playingX, y: playingY + playingHeight, id: 'bottom-left' },        // Bottom-left corner
    { x: playingX + playingWidth, y: playingY + playingHeight, id: 'bottom-right' }, // Bottom-right corner
  ];

  // Key positions (portrait orientation - player stands at bottom looking up)
  // Head (Kopf) = TOP of SVG (smaller Y) - where player stands
  // Foot (Fuß) = BOTTOM of SVG (larger Y) - where rack is placed
  const headSpotY = playingY + playingHeight / 4;  // 1/4 from top (head spot, near player)
  const centerY = playingY + playingHeight / 2;    // Center of table
  const footSpotY = playingY + playingHeight * 3 / 4;  // 3/4 from top (foot spot, rack position)
  const centerX = playingX + playingWidth / 2;

  // Head string (Kopflinie) - horizontal line at head spot (near top)
  const headStringY = headSpotY;

  // Handle drag state
  const [draggedBall, setDraggedBall] = React.useState(null);
  const svgRef = React.useRef(null);

  // Helper to get SVG coordinates from client coordinates
  const getSvgCoords = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };

  // Mouse events
  const handleMouseDown = (e, ball) => {
    if (!interactive) return;
    e.stopPropagation();
    setDraggedBall(ball);
    if (onBallClick) onBallClick(ball);
  };

  const handleMouseMove = (e) => {
    if (!draggedBall || !interactive || !onBallDrag) return;

    const svgP = getSvgCoords(e.clientX, e.clientY);
    if (!svgP) return;

    // Clamp to playing area
    const newX = Math.max(playingX + ballRadius, Math.min(playingX + playingWidth - ballRadius, svgP.x));
    const newY = Math.max(playingY + ballRadius, Math.min(playingY + playingHeight - ballRadius, svgP.y));

    onBallDrag(draggedBall.num, newX, newY);
  };

  const handleMouseUp = () => {
    setDraggedBall(null);
  };

  // Touch events - prevent scrolling and enable ball dragging on mobile
  const handleTouchStart = (e, ball) => {
    if (!interactive) return;
    e.preventDefault();  // Prevent scrolling!
    e.stopPropagation();
    setDraggedBall(ball);
    if (onBallClick) onBallClick(ball);
  };

  const handleTouchMove = (e) => {
    if (!draggedBall || !interactive || !onBallDrag) return;
    e.preventDefault();  // Prevent scrolling!

    const touch = e.touches[0];
    const svgP = getSvgCoords(touch.clientX, touch.clientY);
    if (!svgP) return;

    // Clamp to playing area
    const newX = Math.max(playingX + ballRadius, Math.min(playingX + playingWidth - ballRadius, svgP.x));
    const newY = Math.max(playingY + ballRadius, Math.min(playingY + playingHeight - ballRadius, svgP.y));

    onBallDrag(draggedBall.num, newX, newY);
  };

  const handleTouchEnd = () => {
    setDraggedBall(null);
  };

  const handleTableClick = (e) => {
    if (!interactive || !onTableClick) return;

    const svgP = getSvgCoords(e.clientX, e.clientY);
    if (!svgP) return;

    // Only if inside playing area
    if (svgP.x >= playingX && svgP.x <= playingX + playingWidth &&
        svgP.y >= playingY && svgP.y <= playingY + playingHeight) {
      onTableClick(svgP.x, svgP.y);
    }
  };

  // Handle touch on table for placing balls
  const handleTableTouch = (e) => {
    if (!interactive || !onTableClick || draggedBall) return;
    e.preventDefault();

    const touch = e.touches[0];
    const svgP = getSvgCoords(touch.clientX, touch.clientY);
    if (!svgP) return;

    // Only if inside playing area
    if (svgP.x >= playingX && svgP.x <= playingX + playingWidth &&
        svgP.y >= playingY && svgP.y <= playingY + playingHeight) {
      onTableClick(svgP.x, svgP.y);
    }
  };

  // Render diamonds on rails
  const renderDiamonds = () => {
    const diamonds = [];
    const diamondSize = 4;

    // Left rail diamonds (vertical) - skip center (side pocket)
    for (let i = 1; i < tableConfig.diamonds.long; i++) {
      if (i === tableConfig.diamonds.long / 2) continue; // Skip center (side pocket)
      diamonds.push(
        <polygon
          key={`left-${i}`}
          points={`${playingX - railWidth/2},${playingY + i * diamondSpacingY - diamondSize}
                   ${playingX - railWidth/2 + diamondSize},${playingY + i * diamondSpacingY}
                   ${playingX - railWidth/2},${playingY + i * diamondSpacingY + diamondSize}
                   ${playingX - railWidth/2 - diamondSize},${playingY + i * diamondSpacingY}`}
          fill="#D4AF37"
          stroke="#B8860B"
          strokeWidth="0.5"
        />
      );
    }

    // Right rail diamonds (vertical) - skip center (side pocket)
    for (let i = 1; i < tableConfig.diamonds.long; i++) {
      if (i === tableConfig.diamonds.long / 2) continue; // Skip center (side pocket)
      diamonds.push(
        <polygon
          key={`right-${i}`}
          points={`${playingX + playingWidth + railWidth/2},${playingY + i * diamondSpacingY - diamondSize}
                   ${playingX + playingWidth + railWidth/2 + diamondSize},${playingY + i * diamondSpacingY}
                   ${playingX + playingWidth + railWidth/2},${playingY + i * diamondSpacingY + diamondSize}
                   ${playingX + playingWidth + railWidth/2 - diamondSize},${playingY + i * diamondSpacingY}`}
          fill="#D4AF37"
          stroke="#B8860B"
          strokeWidth="0.5"
        />
      );
    }

    // Top rail diamonds (horizontal) - NO pocket in center on short rails
    for (let i = 1; i < tableConfig.diamonds.short; i++) {
      diamonds.push(
        <polygon
          key={`top-${i}`}
          points={`${playingX + i * diamondSpacingX},${playingY - railWidth/2 - diamondSize}
                   ${playingX + i * diamondSpacingX + diamondSize},${playingY - railWidth/2}
                   ${playingX + i * diamondSpacingX},${playingY - railWidth/2 + diamondSize}
                   ${playingX + i * diamondSpacingX - diamondSize},${playingY - railWidth/2}`}
          fill="#D4AF37"
          stroke="#B8860B"
          strokeWidth="0.5"
        />
      );
    }

    // Bottom rail diamonds (horizontal) - NO pocket in center on short rails
    for (let i = 1; i < tableConfig.diamonds.short; i++) {
      diamonds.push(
        <polygon
          key={`bottom-${i}`}
          points={`${playingX + i * diamondSpacingX},${playingY + playingHeight + railWidth/2 - diamondSize}
                   ${playingX + i * diamondSpacingX + diamondSize},${playingY + playingHeight + railWidth/2}
                   ${playingX + i * diamondSpacingX},${playingY + playingHeight + railWidth/2 + diamondSize}
                   ${playingX + i * diamondSpacingX - diamondSize},${playingY + playingHeight + railWidth/2}`}
          fill="#D4AF37"
          stroke="#B8860B"
          strokeWidth="0.5"
        />
      );
    }

    return diamonds;
  };

  // Render rack outline (triangle or diamond)
  const renderRack = () => {
    if (!showRack) return null;

    const rackX = centerX;
    // Rack position: apex ball on foot spot, rack extends toward head
    const apexY = footSpotY;

    if (gameConfig.rackShape === 'diamond') {
      // 9-ball diamond (3 rows: 1-2-3-2-1)
      // Ball 1 is on foot spot (apex toward foot rail = UP), diamond extends toward head (DOWN)
      const ballDiameter = ballRadius * 2.2;
      const diamondHeight = ballDiameter * 3;
      const diamondWidth = ballDiameter * 2;

      return (
        <polygon
          points={`${rackX},${apexY}
                   ${rackX + diamondWidth/2},${apexY + diamondHeight/2}
                   ${rackX},${apexY + diamondHeight}
                   ${rackX - diamondWidth/2},${apexY + diamondHeight/2}`}
          fill="none"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="1"
          strokeDasharray="4,4"
        />
      );
    } else {
      // Triangle rack (8-ball, 10-ball, 14.1)
      // Foot spot is at larger Y (bottom area of table)
      // Apex (1 ball) at foot spot, rack extends toward foot rail (even larger Y)
      const rows = gameConfig.balls === 10 ? 4 : 5;
      const ballDiameter = ballRadius * 2.2;
      const triangleHeight = ballDiameter * rows * 0.866; // cos(30°)
      const triangleBase = ballDiameter * rows;

      // Apex at footSpotY, base extends toward larger Y (toward foot rail)
      return (
        <polygon
          points={`${rackX},${apexY}
                   ${rackX + triangleBase/2},${apexY + triangleHeight}
                   ${rackX - triangleBase/2},${apexY + triangleHeight}`}
          fill="none"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="1"
          strokeDasharray="4,4"
        />
      );
    }
  };

  // Render grid overlay
  const renderGrid = () => {
    if (!showGrid) return null;

    const gridLines = [];
    const gridSpacing = playingWidth / 8; // 8 divisions

    // Vertical lines
    for (let i = 1; i < 8; i++) {
      gridLines.push(
        <line
          key={`v-${i}`}
          x1={playingX + i * gridSpacing}
          y1={playingY}
          x2={playingX + i * gridSpacing}
          y2={playingY + playingHeight}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.5"
        />
      );
    }

    // Horizontal lines (based on table ratio)
    const hGridSpacing = playingHeight / 16;
    for (let i = 1; i < 16; i++) {
      gridLines.push(
        <line
          key={`h-${i}`}
          x1={playingX}
          y1={playingY + i * hGridSpacing}
          x2={playingX + playingWidth}
          y2={playingY + i * hGridSpacing}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="0.5"
        />
      );
    }

    return gridLines;
  };

  // Convert grid coordinates to SVG coordinates
  const gridToSvg = (gridX, gridY) => ({
    x: playingX + (gridX / GRID_WIDTH) * playingWidth,
    y: playingY + (gridY / GRID_HEIGHT) * playingHeight,
  });

  // Render a ball
  const renderBall = (ball) => {
    // Convert grid coordinates to SVG coordinates if useGridCoordinates is true
    let displayBall = ball;
    if (useGridCoordinates) {
      const svgPos = gridToSvg(ball.x, ball.y);
      displayBall = { ...ball, x: svgPos.x, y: svgPos.y };
    }

    // Get color from the current ball set
    const color = ballSetConfig.colors[ball.num] || ballSetConfig.colors[0] || POOL_BALL_COLORS[0];
    const isSelected = selectedBall === ball.num;
    const isCueBall = ball.num === 0;
    const hasLabel = color.label && color.label.length > 0;

    return (
      <g
        key={ball.num}
        transform={`translate(${displayBall.x}, ${displayBall.y})`}
        style={{ cursor: interactive ? 'grab' : 'default', touchAction: 'none' }}
        onMouseDown={(e) => handleMouseDown(e, ball)}
        onTouchStart={(e) => handleTouchStart(e, ball)}
      >
        {/* Selection ring */}
        {isSelected && (
          <circle
            r={ballRadius + 4}
            fill="none"
            stroke="#FFD700"
            strokeWidth="2"
          />
        )}

        {/* Ball shadow */}
        <ellipse
          cx={2}
          cy={ballRadius * 0.7}
          rx={ballRadius * 0.85}
          ry={ballRadius * 0.25}
          fill="rgba(0,0,0,0.35)"
        />

        {/* Ball body */}
        <circle
          r={ballRadius}
          fill={color.fill}
          stroke={color.stroke}
          strokeWidth="1.5"
        />

        {/* Stripe for striped balls (pool only) */}
        {color.striped && (
          <path
            d={`M ${-ballRadius * 0.7} 0
                Q 0 ${-ballRadius * 0.5} ${ballRadius * 0.7} 0
                Q 0 ${ballRadius * 0.5} ${-ballRadius * 0.7} 0`}
            fill="white"
          />
        )}

        {/* Number circle for numbered/labeled balls */}
        {hasLabel && (
          <>
            <circle
              r={ballRadius * 0.4}
              fill="white"
            />
            <text
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={ballRadius * (color.label.length > 1 ? 0.4 : 0.5)}
              fontWeight="bold"
              fill="black"
            >
              {color.label}
            </text>
          </>
        )}

        {/* Cue ball hit point marker */}
        {isCueBall && cueHitPoint && (
          <>
            {/* Crosshair lines */}
            <line
              x1={-ballRadius * 0.6}
              y1={0}
              x2={ballRadius * 0.6}
              y2={0}
              stroke="rgba(0,0,0,0.2)"
              strokeWidth="0.5"
            />
            <line
              x1={0}
              y1={-ballRadius * 0.6}
              x2={0}
              y2={ballRadius * 0.6}
              stroke="rgba(0,0,0,0.2)"
              strokeWidth="0.5"
            />
            {/* Hit point dot */}
            <circle
              cx={cueHitPoint.x * ballRadius * 0.7}
              cy={cueHitPoint.y * ballRadius * 0.7}
              r={ballRadius * 0.15}
              fill="#FF4444"
              stroke="#CC0000"
              strokeWidth="1"
            />
          </>
        )}

        {/* Highlight */}
        <circle
          cx={-ballRadius * 0.3}
          cy={-ballRadius * 0.3}
          r={ballRadius * 0.2}
          fill="rgba(255,255,255,0.5)"
        />
      </g>
    );
  };

  return (
    <svg
      ref={svgRef}
      width={svgWidth}
      height={svgHeight}
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onClick={handleTableClick}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTableTouch}
      style={{ userSelect: 'none', touchAction: 'none' }}
    >
      {/* Outer frame (wood) */}
      <rect
        x={padding}
        y={padding}
        width={svgWidth - 2 * padding}
        height={svgHeight - 2 * padding}
        rx={6}
        fill="#5D3A1A"
        stroke="#3D2410"
        strokeWidth={2}
      />

      {/* Inner frame */}
      <rect
        x={padding + frameWidth}
        y={padding + frameWidth}
        width={svgWidth - 2 * padding - 2 * frameWidth}
        height={svgHeight - 2 * padding - 2 * frameWidth}
        rx={4}
        fill="#4A2F14"
      />

      {/* Rails (cushions) */}
      <rect
        x={padding + frameWidth}
        y={padding + frameWidth}
        width={svgWidth - 2 * padding - 2 * frameWidth}
        height={svgHeight - 2 * padding - 2 * frameWidth}
        rx={4}
        fill="#1B5E20"
        stroke="#0D3B0D"
        strokeWidth={1}
      />

      {/* Playing surface */}
      <rect
        x={playingX}
        y={playingY}
        width={playingWidth}
        height={playingHeight}
        fill="#2E7D32"
      />

      {/* Grid overlay */}
      {renderGrid()}

      {/* Head string (Kopflinie) / Baulk line - horizontal at bottom quarter */}
      <line
        x1={playingX}
        y1={headStringY}
        x2={playingX + playingWidth}
        y2={headStringY}
        stroke="rgba(255,255,255,0.25)"
        strokeWidth={1}
        strokeDasharray={gameType === 'snooker' ? "0" : "6,4"}
      />

      {/* Snooker D (semicircle opening toward foot = downward = larger Y) */}
      {gameType === 'snooker' && (
        <path
          d={`M ${centerX - playingWidth * 0.15} ${headStringY}
              A ${playingWidth * 0.15} ${playingWidth * 0.15} 0 0 1 ${centerX + playingWidth * 0.15} ${headStringY}`}
          fill="none"
          stroke="rgba(255,255,255,0.25)"
          strokeWidth={1}
        />
      )}

      {/* Foot spot (top) */}
      <circle
        cx={centerX}
        cy={footSpotY}
        r={4}
        fill="rgba(255,255,255,0.4)"
      />

      {/* Head spot (bottom) */}
      <circle
        cx={centerX}
        cy={headSpotY}
        r={4}
        fill="rgba(255,255,255,0.4)"
      />

      {/* Center spot */}
      <circle
        cx={centerX}
        cy={centerY}
        r={3}
        fill="rgba(255,255,255,0.3)"
      />

      {/* Rack outline */}
      {renderRack()}

      {/* Diamonds on rails */}
      {renderDiamonds()}

      {/* Pockets */}
      {pockets.map(pocket => (
        <g key={pocket.id}>
          {/* Pocket hole */}
          <circle
            cx={pocket.x}
            cy={pocket.y}
            r={pocketRadius}
            fill="#111"
          />
          {/* Pocket inner rim */}
          <circle
            cx={pocket.x}
            cy={pocket.y}
            r={pocketRadius * 0.85}
            fill="none"
            stroke="#222"
            strokeWidth="2"
          />
        </g>
      ))}

      {/* Balls */}
      {balls.map(renderBall)}

      {/* Labels for orientation (debug) */}
      {/*
      <text x={centerX} y={playingY + 20} textAnchor="middle" fill="white" fontSize="10">FOOT (Rack)</text>
      <text x={centerX} y={playingY + playingHeight - 10} textAnchor="middle" fill="white" fontSize="10">HEAD (You)</text>
      */}
    </svg>
  );
};

export default PoolTableSVG;
export { BALL_COLORS, POOL_BALL_COLORS, SNOOKER_BALL_COLORS, BALL_SETS, TABLE_SIZES, GAME_TYPES, GRID_WIDTH, GRID_HEIGHT };
